import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {Menu, X, Droplets, Phone, Mail, MessageCircleDashed as MessageCircle, ChevronDown} from 'lucide-react'

const CONTACT_EMAIL = "info@havenintegrativehealthconsulting.com"
const CONTACT_PHONE = "778-898-3419"
const CONTACT_PHONE_RAW = "+17788983419"

type NavLink = {
  label: string
  to: string
  external?: boolean
  matchPath?: string
  subLabel?: string
  subTo?: string
}

const navLinks: NavLink[] = [
  {
    label: "Haven Wellness Health",
    to: "/havenwellnesshealth",
    matchPath: "/havenwellnesshealth",
    subLabel: "IV Nutrient Therapy",
    subTo: "/havenwellnesshealth",
  },
  { label: "Haven Menopause Health", to: "/menopausehealth", matchPath: "/menopausehealth" },
  { label: "Haven Acne Health", to: "/acnehealth", matchPath: "/acnehealth" },
  { label: "Haven Travel Health", to: "/travelhealth", matchPath: "/travelhealth" },
]

function HavenNav() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const location = useLocation()
  const currentPath = location.pathname

  return (
    <nav className="fixed top-0 w-full bg-[#1F1D1A] z-50 border-b border-white/5">
      <div className="max-w-[1600px] mx-auto px-5 lg:px-10">
        <div className="flex items-center justify-between h-[76px] gap-4">

          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <span
              className="text-[28px] text-white tracking-[-0.01em] leading-none"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              Haven
            </span>
          </Link>

          {/* Desktop Service Links */}
          <div className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
            {navLinks.map((link) => {
              const isActive = link.matchPath && currentPath === link.matchPath

              if (link.external) {
                return (
                  <a
                    key={link.label}
                    href={link.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 text-[11.5px] tracking-[0.07em] uppercase text-white/65 hover:text-white hover:bg-white/5 rounded-full transition-colors whitespace-nowrap"
                  >
                    {link.label}
                  </a>
                )
              }

              return (
                <div key={link.label} className="flex items-center gap-1">
                  {/* Main pillar link */}
                  <Link
                    to={link.to}
                    className={`px-3 py-2 text-[11.5px] tracking-[0.07em] uppercase rounded-full transition-colors whitespace-nowrap ${
                      isActive
                        ? "text-white bg-white/10"
                        : "text-white/65 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </Link>

                  {/* Always-visible sub-label pill (IV Nutrient Therapy) */}
                  {link.subLabel && (
                    <Link
                      to={link.subTo || link.to}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#C9A96E]/40 bg-[#C9A96E]/10 text-[#C9A96E] hover:bg-[#C9A96E]/20 hover:border-[#C9A96E]/60 transition-all whitespace-nowrap text-[10.5px] tracking-[0.09em] uppercase font-medium"
                    >
                      <Droplets className="w-2.5 h-2.5 flex-shrink-0" />
                      {link.subLabel}
                    </Link>
                  )}
                </div>
              )
            })}
          </div>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            <Link
              to="/team"
              className={`text-[11.5px] tracking-[0.07em] uppercase transition-colors ${
                currentPath === "/team" ? "text-white" : "text-white/65 hover:text-white"
              }`}
            >
              Team
            </Link>

            <Link
              to="/blog/iv-therapy-myths"
              className={`text-[11.5px] tracking-[0.07em] uppercase transition-colors ${
                currentPath === "/blog/iv-therapy-myths" ? "text-white" : "text-white/65 hover:text-white"
              }`}
            >
              Blog
            </Link>

            {/* Contact tab with dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setContactOpen(true)}
              onMouseLeave={() => setContactOpen(false)}
            >
              <button
                className="flex items-center gap-1 text-[11.5px] tracking-[0.07em] uppercase text-white/65 hover:text-white transition-colors"
                aria-label="Contact us"
              >
                Contact
                <ChevronDown className={`w-3 h-3 transition-transform ${contactOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {contactOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18 }}
                    className="absolute right-0 top-full pt-3 w-[280px]"
                  >
                    <div className="bg-[#2A2825] border border-white/10 rounded-2xl p-4 shadow-2xl space-y-1">
                      <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors group">
                        <Mail className="w-4 h-4 text-[#C9A96E] flex-shrink-0" />
                        <span className="text-[12px] text-white/80 group-hover:text-white break-all">{CONTACT_EMAIL}</span>
                      </a>
                      <a href={`tel:${CONTACT_PHONE_RAW}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors group">
                        <Phone className="w-4 h-4 text-[#C9A96E] flex-shrink-0" />
                        <span className="text-[12.5px] text-white/80 group-hover:text-white">{CONTACT_PHONE}</span>
                      </a>
                      <a href={`https://wa.me/${CONTACT_PHONE_RAW.replace("+", "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors group">
                        <MessageCircle className="w-4 h-4 text-[#C9A96E] flex-shrink-0" />
                        <span className="text-[12.5px] text-white/80 group-hover:text-white">WhatsApp: {CONTACT_PHONE}</span>
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <a
              href="https://havenintegrativehealth.janeapp.com/locations/haven-wellness-health-iv-therapy/book#/staff_member/1/treatment/10"
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 bg-[#C9A96E] text-[#1F1D1A] rounded-full text-[11px] tracking-[0.12em] uppercase font-semibold hover:bg-[#D4B57A] transition-colors whitespace-nowrap"
            >
              Book IV Therapy
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 text-white flex-shrink-0"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#1F1D1A] border-t border-white/10 overflow-hidden"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {navLinks.map((link) => {
                const isActive = link.matchPath && currentPath === link.matchPath
                const itemClass = `px-4 py-3 text-[13px] tracking-[0.06em] uppercase rounded-xl transition-colors ${
                  isActive ? "text-white bg-white/10" : "text-white/70 hover:text-white hover:bg-white/5"
                }`

                if (link.external) {
                  return (
                    <a
                      key={link.label}
                      href={link.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className={itemClass}
                    >
                      {link.label}
                    </a>
                  )
                }

                return (
                  <div key={link.label}>
                    <Link
                      to={link.to}
                      onClick={() => setMenuOpen(false)}
                      className={itemClass}
                    >
                      {link.label}
                    </Link>
                    {/* Always show sub-pill in mobile too */}
                    {link.subLabel && (
                      <Link
                        to={link.subTo || link.to}
                        onClick={() => setMenuOpen(false)}
                        className="ml-4 mt-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#C9A96E]/40 bg-[#C9A96E]/10 text-[#C9A96E] text-[11px] tracking-[0.08em] uppercase"
                      >
                        <Droplets className="w-3 h-3" />
                        {link.subLabel}
                      </Link>
                    )}
                  </div>
                )
              })}

              <div className="h-px bg-white/10 my-3" />

              <Link
                to="/team"
                onClick={() => setMenuOpen(false)}
                className="px-4 py-3 text-[13px] tracking-[0.06em] uppercase text-white/70 hover:text-white rounded-xl"
              >
                Team
              </Link>

              <Link
                to="/blog/iv-therapy-myths"
                onClick={() => setMenuOpen(false)}
                className="px-4 py-3 text-[13px] tracking-[0.06em] uppercase text-white/70 hover:text-white rounded-xl"
              >
                Blog
              </Link>

              {/* Contact info */}
              <div className="mt-2 px-4 py-3 rounded-xl bg-white/5">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-3">Contact Us</p>
                <div className="space-y-2.5">
                  <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-3 text-white/80 hover:text-white">
                    <Mail className="w-4 h-4 text-[#C9A96E] flex-shrink-0" />
                    <span className="text-[12px] break-all">{CONTACT_EMAIL}</span>
                  </a>
                  <a href={`tel:${CONTACT_PHONE_RAW}`} className="flex items-center gap-3 text-white/80 hover:text-white">
                    <Phone className="w-4 h-4 text-[#C9A96E] flex-shrink-0" />
                    <span className="text-[13px]">{CONTACT_PHONE}</span>
                  </a>
                  <a href={`https://wa.me/${CONTACT_PHONE_RAW.replace("+", "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/80 hover:text-white">
                    <MessageCircle className="w-4 h-4 text-[#C9A96E] flex-shrink-0" />
                    <span className="text-[13px]">WhatsApp: {CONTACT_PHONE}</span>
                  </a>
                </div>
              </div>
              <a
                href="https://havenintegrativehealth.janeapp.com/locations/haven-wellness-health-iv-therapy/book#/staff_member/1/treatment/10"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="mt-2 px-4 py-3 bg-[#C9A96E] text-[#1F1D1A] rounded-full text-[12px] tracking-[0.12em] uppercase font-semibold text-center"
              >
                Book IV Therapy
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default HavenNav
